import { Injectable } from '@angular/core';
import { FormControl, FormArray, FormGroup } from '@angular/forms';

import { ToPlainText } from '@empathy/global/functions';
import { ChoiceTypeResult, Question, QuestionType } from '@empathy/survey/base';

@Injectable({ providedIn: 'root' })
export class PipedContentService {
  mapPipedContentFromControl(
    questions: { [questionId: string]: Question },
    answers: { [qId: string]: FormControl | FormArray | FormGroup },
    text: string,
    questionId: string
  ): string {
    const mappedAnswers: { [qId: string]: any } = {};
    Object.keys(answers).forEach((key) => {
      mappedAnswers[key] = answers[key].value;
    });

    return this.mapPipedContent(questions, mappedAnswers, text, questionId);
  }

  mapPipedContent(questions: { [questionId: string]: Question }, answers: { [qId: string]: unknown }, text: string, questionId: string): string {
    if (Object.keys(answers).length === 0) {
      return text;
    }

    const pattern = /\[([\s\S]*?)(?=])/g;
    const pipes = text?.match(pattern);
    if (!pipes) {
      return text;
    }

    for (let i = 0; i < pipes.length; i++) {
      if (pipes[i].length > 1) {
        pipes[i] = pipes[i].substring(1, pipes[i].length);
      }
    }

    const questionsArray = Object.values(questions);

    pipes.forEach((pipe) => {
      const pipeSplit = pipe.trim().split('|');

      const args = pipeSplit[1]?.split(':') || [];

      if (args.length === 0) {
        return;
      }

      let format: string;
      let answer: unknown;
      let qId: string;
      const qCode = pipeSplit[0].trim();

      if (pipeSplit.length === 3 && pipeSplit[2].trim() === 'loopVar') {
        const loopVar = questionId.split('_')[1];
        qId = questionsArray.find((q) => q.code === `${qCode}_${loopVar}`)?.id;
      } else {
        qId = questionsArray.find((q) => q.code === qCode)?.id;
      }

      if (!qId) {
        return;
      }

      if (args.length === 1) {
        format = args[0].trim();
        answer = answers[qId];
      } else {
        format = args[1].trim();

        if (this.isString(answers[qId])) {
          const answerRaw = JSON.parse(answers[qId] as string);
          answer = answerRaw[args[0].trim()];
        } else {
          answer = answers[qId][args[0].trim()];
        }

        if (args[0].includes('rank')) {
          const rankIndex = Number(args[0].split('_')[1]) - 1;

          let answerValue;
          if (answers[qId]['value']) {
            answerValue = answers[qId]['value'] as string[];
          } else {
            answerValue = answers[qId] as string[];
          }

          if (this.isString(answerValue)) {
            answerValue = JSON.parse(answerValue as string).value;
          }

          const answerIndex = Number(answerValue[rankIndex]) - 1;
          answer = questions[qId].options[answerIndex].text;
        }
      }

      text = text.replace(`[${pipe}]`, `${this.formatValue(this.mapText(answer, questions[qId]), format)}`);
    });

    return text;
  }

  private mapText(answer: unknown, question: Question): string {
    if (!question.options?.length && !question.cards?.length) {
      return answer as string;
    }

    if (typeof answer === 'string') {
      if ([QuestionType.RankSort, QuestionType.Ranking].includes(question.type)) {
        return ToPlainText(answer);
      } else {
        answer = JSON.parse(answer as string);
      }
    }

    if (typeof answer === 'object') {
      if (Array.isArray((answer as ChoiceTypeResult).value)) {
        const textMapped = question.options
          .filter((opt) => (answer as ChoiceTypeResult).value.includes(opt.value))
          .map((opt) => ToPlainText(opt.text))
          .join(', ')
          .replace(/, ([^,]*)$/, ' and $1');

        if ((answer as ChoiceTypeResult).value.includes('OTHER')) {
          return textMapped?.length > 0 ? `${textMapped}, ${(answer as ChoiceTypeResult).otherText}` : (answer as ChoiceTypeResult).otherText;
        }

        return textMapped;
      } else {
        const optionValue = (answer as ChoiceTypeResult).value;
        if (optionValue === 'OTHER') {
          return (answer as ChoiceTypeResult).otherText || '';
        }

        const optionText = question.options.find((opt) => opt.value === optionValue)?.text;
        const optionValueFormatted = Array.isArray(optionValue) ? optionValue.join(',') : optionValue;
        return ToPlainText(optionText || optionValueFormatted);
      }
    } else if (typeof answer === 'string') {
      return ToPlainText(question.options.find((opt) => opt.value === answer)?.text);
    }
  }

  private formatValue(value: string, format: string): string {
    switch (format) {
      case 'uppercase':
        return String(value).toLocaleUpperCase();
      case 'lowercase':
        return String(value).toLocaleLowerCase();
      case 'capitalize':
        return String(value).charAt(0).toLocaleUpperCase() + value.slice(1);
      default:
        return value;
    }
  }

  private isString(value): boolean {
    return typeof value === 'string' || value instanceof String;
  }
}
